import { useEffect } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import {
  ArrowLeft,
  ArrowRight,
  Sparkles,
  Home,
  Building2,
  Truck,
  Star,
  Phone,
  Mail,
  Check,
} from "lucide-react";

const NAV = [
  { label: "Ydelser", href: "#rb-ydelser" },
  { label: "Hvorfor os", href: "#rb-hvorfor" },
  { label: "Sådan gør vi", href: "#rb-proces" },
  { label: "Anmeldelser", href: "#rb-anmeldelser" },
  { label: "Kontakt", href: "#rb-kontakt" },
];

const SERVICES = [
  {
    icon: Home,
    title: "Privatrengøring",
    text: "Fast rengøring i hjemmet — ugentligt eller hver 14. dag, helt efter dine ønsker og din hverdag.",
  },
  {
    icon: Building2,
    title: "Erhvervsrengøring",
    text: "Rene kontorer og lokaler, der giver et professionelt indtryk over for både kunder og medarbejdere.",
  },
  {
    icon: Truck,
    title: "Flytterengøring",
    text: "Grundig rengøring ved fraflytning, så boligen står klar og indflytningsvenlig til den næste beboer.",
  },
  {
    icon: Sparkles,
    title: "Hovedrengøring",
    text: "En dybdegående gennemgang af hele boligen — fra kalk i badeværelset til fedt i køkkenet.",
  },
];

const WHY = [
  "Erfarne og stabile rengøringsteams",
  "Skånsomme og effektive produkter",
  "Fleksible aftaler uden binding",
  "Fast kontaktperson og tydelig aftale",
];

const STEPS = [
  { n: "01", title: "Fortæl om dine behov", text: "Ring eller skriv — vi tager en kort snak om boligen eller lokalerne." },
  { n: "02", title: "Få et gratis tilbud", text: "Du får et klart og uforpligtende tilbud med en fast pris." },
  { n: "03", title: "Vi sørger for resten", text: "Vi laver en plan, der passer dig — og holder stedet rent, gang på gang." },
];

const REVIEWS = [
  { name: "Louise M.", text: "Altid pænt og rent, når jeg kommer hjem. Nem kommunikation og søde mennesker." },
  { name: "Peter K.", text: "Vores kontor har aldrig set bedre ud. Pålidelige og grundige hver eneste gang." },
  { name: "Sara L.", text: "Flytterengøringen var fejlfri — og tilbuddet holdt præcis, hvad det lovede." },
];

const fadeUp = (delay = 0) => ({
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.6, delay, ease: [0.22, 1, 0.36, 1] },
});

const Label = ({ children }) => (
  <p className="text-xs font-semibold uppercase tracking-[0.2em] text-teal-600 mb-4">{children}</p>
);

export default function RenbyCase({ onNavigate }) {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const go = (href) => (e) => {
    e.preventDefault();
    onNavigate?.(href);
  };

  return (
    <div className="bg-white text-zinc-900 antialiased min-h-screen" data-testid="renby-case-page">
      <div className="bg-zinc-900 text-white text-xs sm:text-sm py-2.5 px-4 flex flex-wrap items-center justify-center gap-x-3 gap-y-1 text-center">
        <span className="font-medium">Konceptprojekt – ikke en rigtig kunde. Designet af Naveli.</span>
        <Link to="/" data-testid="renby-back-link" className="inline-flex items-center gap-1 font-semibold underline underline-offset-2">
          <ArrowLeft className="w-3.5 h-3.5" aria-hidden="true" />
          Tilbage til Naveli
        </Link>
      </div>

      <header className="sticky top-0 z-50 bg-white/85 backdrop-blur-md border-b border-zinc-200/80">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-[68px] flex items-center justify-between">
          <a href="#rb-top" onClick={go("#rb-top")} data-testid="renby-logo" className="flex items-center gap-2.5 font-bold text-lg tracking-tight">
            <span className="w-8 h-8 rounded-xl bg-teal-600 text-white flex items-center justify-center">
              <Sparkles className="w-4 h-4" aria-hidden="true" />
            </span>
            Renby <span className="text-zinc-400 font-medium hidden sm:inline">København</span>
          </a>
          <nav className="hidden md:flex items-center gap-7" aria-label="Renby menu">
            {NAV.map((l) => (
              <a
                key={l.href}
                href={l.href}
                onClick={go(l.href)}
                data-testid={`renby-nav-${l.href.replace("#rb-", "")}`}
                className="text-sm text-zinc-600 hover:text-zinc-900 transition-colors duration-200"
              >
                {l.label}
              </a>
            ))}
          </nav>
          <a
            href="#rb-kontakt"
            onClick={go("#rb-kontakt")}
            data-testid="renby-quote-cta"
            className="inline-flex items-center gap-2 bg-teal-600 text-white text-sm font-semibold rounded-full px-5 py-2.5 hover:bg-teal-700 hover:-translate-y-px transition-all duration-200"
          >
            Få et gratis tilbud
            <ArrowRight className="w-4 h-4" aria-hidden="true" />
          </a>
        </div>
      </header>

      <section id="rb-top" className="bg-gradient-to-b from-teal-50/80 to-white" data-testid="renby-hero">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-20 sm:py-28 grid gap-12 lg:grid-cols-2 items-center">
          <div>
            <motion.p {...fadeUp()} className="text-xs font-semibold uppercase tracking-[0.25em] text-teal-600 mb-6">
              Rengøringsservice · København
            </motion.p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.08]">
              <span className="block overflow-hidden pb-1">
                <motion.span className="block" initial={{ y: "110%" }} animate={{ y: 0 }} transition={{ duration: 0.9, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}>
                  Professionel rengøring
                </motion.span>
              </span>
              <span className="block overflow-hidden pb-1">
                <motion.span className="block" initial={{ y: "110%" }} animate={{ y: 0 }} transition={{ duration: 0.9, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}>
                  i København
                </motion.span>
              </span>
            </h1>
            <motion.p {...fadeUp(0.5)} className="mt-6 text-lg text-zinc-600 leading-relaxed max-w-lg">
              Vi hjælper private og erhverv med pålidelig, grundig rengøring — så du
              kan bruge tiden på det, der betyder mest for dig.
            </motion.p>
            <motion.div {...fadeUp(0.65)} className="mt-9 flex flex-col sm:flex-row gap-4">
              <a
                href="#rb-kontakt"
                onClick={go("#rb-kontakt")}
                data-testid="renby-hero-cta"
                className="inline-flex items-center justify-center gap-2 bg-teal-600 text-white font-semibold rounded-full px-8 py-4 hover:bg-teal-700 hover:-translate-y-0.5 transition-all duration-200"
              >
                Få et gratis tilbud
                <ArrowRight className="w-4 h-4" aria-hidden="true" />
              </a>
              <a
                href="#rb-ydelser"
                onClick={go("#rb-ydelser")}
                data-testid="renby-hero-secondary"
                className="inline-flex items-center justify-center gap-2 border border-zinc-300 text-zinc-900 font-medium rounded-full px-8 py-4 hover:border-teal-600 hover:text-teal-700 hover:-translate-y-0.5 transition-all duration-200"
              >
                Se ydelser
              </a>
            </motion.div>
            <motion.ul {...fadeUp(0.8)} className="mt-9 flex flex-wrap gap-x-7 gap-y-2.5 text-sm text-zinc-500">
              {["Uforpligtende tilbud", "Fleksible aftaler", "Fast kontaktperson"].map((t) => (
                <li key={t} className="flex items-center gap-2">
                  <Check className="w-4 h-4 text-teal-600" aria-hidden="true" />
                  {t}
                </li>
              ))}
            </motion.ul>
          </div>
          <motion.div {...fadeUp(0.4)} className="rounded-3xl overflow-hidden aspect-[4/3] bg-teal-100">
            <img
              src="https://images.unsplash.com/photo-1581578731548-c64695cc6952?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200"
              alt="Rengøringsmedarbejder hos Renby København"
              className="w-full h-full object-cover"
            />
          </motion.div>
        </div>
      </section>

      <section id="rb-ydelser" className="py-20 sm:py-28" data-testid="renby-services">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <motion.div {...fadeUp()}>
            <Label>Ydelser</Label>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight max-w-xl">
              Rengøring til både hjem og erhverv
            </h2>
          </motion.div>
          <div className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {SERVICES.map((s, i) => (
              <motion.div
                key={s.title}
                {...fadeUp(i * 0.08)}
                data-testid={`renby-service-${i + 1}`}
                className="bg-teal-50/60 border border-teal-100 rounded-2xl p-7 hover:bg-white hover:border-teal-600 hover:-translate-y-1 transition-all duration-300"
              >
                <div className="w-11 h-11 rounded-xl bg-teal-600 text-white flex items-center justify-center mb-5">
                  <s.icon className="w-5 h-5" aria-hidden="true" />
                </div>
                <h3 className="text-lg font-semibold">{s.title}</h3>
                <p className="mt-2.5 text-sm text-zinc-600 leading-relaxed">{s.text}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section id="rb-hvorfor" className="py-20 sm:py-28 bg-teal-50/60" data-testid="renby-why">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 grid gap-12 lg:grid-cols-2 items-center">
          <motion.div {...fadeUp()}>
            <Label>Hvorfor vælge Renby?</Label>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight">
              Rent uden besvær — hver eneste gang
            </h2>
            <p className="mt-5 text-base sm:text-lg text-zinc-600 leading-relaxed">
              Vi ved, at rengøring handler om tillid. Derfor får du en tydelig aftale,
              et fast team og et resultat, du kan se og mærke.
            </p>
          </motion.div>
          <div className="grid sm:grid-cols-2 gap-4">
            {WHY.map((w, i) => (
              <motion.div key={w} {...fadeUp(i * 0.08)} className="bg-white border border-teal-100 rounded-2xl p-6 flex items-start gap-3">
                <Check className="w-5 h-5 text-teal-600 shrink-0 mt-0.5" aria-hidden="true" />
                <span className="text-sm font-medium text-zinc-800 leading-relaxed">{w}</span>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section id="rb-proces" className="py-20 sm:py-28" data-testid="renby-process">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <motion.div {...fadeUp()}>
            <Label>Sådan gør vi</Label>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight">Tre enkle trin til et rent hjem</h2>
          </motion.div>
          <div className="mt-12 grid gap-8 sm:grid-cols-3">
            {STEPS.map((s, i) => (
              <motion.div key={s.n} {...fadeUp(i * 0.1)} className="border-t-2 border-teal-600 pt-6">
                <span className="font-mono-accent text-sm text-teal-600">{s.n}</span>
                <h3 className="mt-3 text-xl font-semibold">{s.title}</h3>
                <p className="mt-2.5 text-sm text-zinc-600 leading-relaxed">{s.text}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section id="rb-anmeldelser" className="py-20 sm:py-28 bg-teal-50/60" data-testid="renby-reviews">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <motion.div {...fadeUp()}>
            <Label>Kunderne siger</Label>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight">Tilfredse kunder i hele København</h2>
            <p className="mt-3 text-sm text-zinc-500">Fiktive demoanmeldelser — vist som layouteksempel.</p>
          </motion.div>
          <div className="mt-12 grid gap-6 md:grid-cols-3">
            {REVIEWS.map((r, i) => (
              <motion.figure key={r.name} {...fadeUp(i * 0.1)} className="bg-white border border-teal-100 rounded-2xl p-7">
                <div className="flex gap-1 mb-4" aria-label="5 ud af 5 stjerner">
                  {[...Array(5)].map((_, s) => (
                    <Star key={s} className="w-4 h-4 fill-teal-500 text-teal-500" aria-hidden="true" />
                  ))}
                </div>
                <blockquote className="text-base text-zinc-700 leading-relaxed">“{r.text}”</blockquote>
                <figcaption className="mt-5 text-sm font-medium text-zinc-500">{r.name}</figcaption>
              </motion.figure>
            ))}
          </div>
        </div>
      </section>

      <section id="rb-kontakt" className="py-20 sm:py-28 bg-teal-700 text-white" data-testid="renby-contact">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 text-center">
          <motion.div {...fadeUp()}>
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-teal-200 mb-4">
              Gratis tilbud
            </p>
            <h2 className="text-3xl sm:text-5xl font-bold tracking-tight max-w-2xl mx-auto">
              Klar til et renere hjem eller kontor?
            </h2>
            <p className="mt-5 text-base sm:text-lg text-teal-100 max-w-xl mx-auto">
              Ring eller skriv, så laver vi et gratis og uforpligtende tilbud til dig.
            </p>
            <div className="mt-9 flex flex-col sm:flex-row items-center justify-center gap-4">
              <a
                href="tel:+4533445566"
                data-testid="renby-phone-link"
                className="inline-flex items-center gap-2 bg-white text-teal-800 font-semibold rounded-full px-8 py-4 hover:bg-teal-50 hover:-translate-y-0.5 transition-all duration-200"
              >
                <Phone className="w-4 h-4" aria-hidden="true" />
                Ring 33 44 55 66
              </a>
              <a
                href="mailto:hej@renby.dk"
                data-testid="renby-email-link"
                className="inline-flex items-center gap-2 border border-teal-300 text-white font-medium rounded-full px-8 py-4 hover:bg-teal-600 hover:-translate-y-0.5 transition-all duration-200"
              >
                <Mail className="w-4 h-4" aria-hidden="true" />
                hej@renby.dk
              </a>
            </div>
            <p className="mt-6 text-xs text-teal-200/80">
              Demo — telefonnummer og e-mail tilhører ikke en rigtig virksomhed.
            </p>
          </motion.div>
        </div>
      </section>

      <footer className="border-t border-zinc-200/80 py-10 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 flex flex-col sm:flex-row items-center justify-between gap-4 text-sm text-zinc-500">
          <span className="flex items-center gap-2 font-bold tracking-tight text-zinc-900">
            <span className="w-6 h-6 rounded-lg bg-teal-600 text-white flex items-center justify-center">
              <Sparkles className="w-3.5 h-3.5" aria-hidden="true" />
            </span>
            Renby København
          </span>
          <p className="text-center">Konceptprojekt lavet af Naveli — ikke en rigtig kunde.</p>
          <Link to="/" data-testid="renby-footer-back-link" className="inline-flex items-center gap-1 hover:text-zinc-900 transition-colors duration-200">
            <ArrowLeft className="w-4 h-4" aria-hidden="true" />
            Tilbage til Naveli
          </Link>
        </div>
      </footer>
    </div>
  );
}
